import { publicAssetBaseUrl } from "@lib/meta"
import * as m from "@paraglide/messages"
import {
  routeLocaleFromPathname,
  routePageClass,
  routePageFromPathname,
  useRouteShellPathname,
} from "@shared/ui/routeShellContext"
import { SiteShell } from "@shared/ui/SiteShell"
import type { SupportedLocale } from "@site-types/content"
import type { QueryClient } from "@tanstack/react-query"
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
  useRouterState,
} from "@tanstack/react-router"
import type { ReactNode } from "react"
import appCss from "../global.css?url"
import { analyticsScript, remoteImageSkeletonScript, viewTransitionRouterScript } from "../root-scripts"
import { ErrorPage } from "./-ErrorPage"

const fontAwesomeKitUrl = "https://kit.fontawesome.com/70d7c74973.css"
const anonymousCrossOrigin = "anonymous" as const

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient
}>()({
  head: () => ({
    title: "PLAYBRAIN Inc.",
    meta: [{ charSet: "utf-8" }, { name: "viewport", content: "width=device-width" }],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "stylesheet", href: fontAwesomeKitUrl },
      { rel: "sitemap", href: "/sitemap.xml" },
      { rel: "icon", type: "image/x-icon", href: "/favicon.png" },
      { rel: "preconnect", href: "https://use.typekit.net", crossOrigin: anonymousCrossOrigin },
      { rel: "preconnect", href: "https://p.typekit.net", crossOrigin: anonymousCrossOrigin },
      { rel: "preconnect", href: publicAssetBaseUrl(), crossOrigin: anonymousCrossOrigin },
    ],
  }),
  component: RootComponent,
  errorComponent: RootError,
  notFoundComponent: NotFound,
})

function RootComponent() {
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  return (
    <RootDocument pageClass={pageClassFromPathname(pathname)}>
      <Outlet />
    </RootDocument>
  )
}

function pageClassFromPathname(pathname: string) {
  if (isDashboardPagePath(pathname)) return "dashboard"

  const page = routePageFromPathname(pathname)

  return routePageClass(page)
}

function isDashboardPagePath(pathname: string) {
  const normalizedPathname = pathname.split("?")[0]?.split("#")[0] ?? pathname

  return (
    normalizedPathname === "/activate-account" ||
    normalizedPathname === "/login" ||
    normalizedPathname === "/reset-password" ||
    normalizedPathname === "/auth/callback" ||
    normalizedPathname === "/dashboard" ||
    normalizedPathname.startsWith("/dashboard/")
  )
}

function RootDocument({ children, pageClass }: Readonly<{ children: ReactNode; pageClass: string }>) {
  const pathname = useRouteShellPathname()
  const locale = routeLocaleFromPathname(pathname)

  return (
    <html
      lang={locale}
      className={pageClass === "index" || pageClass === "dashboard" ? pageClass : undefined}
    >
      <head>
        <title>PLAYBRAIN Inc.</title>
        <HeadContent />
        <script dangerouslySetInnerHTML={{ __html: analyticsScript }} />
        <script dangerouslySetInnerHTML={{ __html: remoteImageSkeletonScript }} />
        <script dangerouslySetInnerHTML={{ __html: viewTransitionRouterScript }} />
      </head>
      <body className={pageClass}>
        {children}
        <Scripts />
      </body>
    </html>
  )
}

function NotFound() {
  return (
    <SiteShell page="error404">
      <ErrorPage />
    </SiteShell>
  )
}

function RootError() {
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const locale = routeLocaleFromPathname(pathname) as SupportedLocale

  return (
    <SiteShell
      page={m.error_500_meta_page({}, { locale })}
      title={m.error_500_meta_pageTitle({}, { locale })}
    >
      <title>{m.error_500_meta_title({}, { locale })}</title>
      <meta
        name="robots"
        content="noindex,nofollow"
      />
      <div
        className="mx-auto min-h-[calc(100lvh-51px)] max-w-[600px] pt-[5%] text-center"
        data-testid="page-error"
      >
        <h1 className="text-[34px]">{m.error_500_label({}, { locale })}</h1>
        <div>
          <p>{m.error_500_description({}, { locale })}</p>
        </div>
        <a href={`/${locale}`}>{m.back_toHome({}, { locale })}</a>
      </div>
    </SiteShell>
  )
}
